/*
Purpose: Left navigation sidebar showing list of user's casefiles for quick navigation.
Audience: Case owners and collaborators.
What it does:
- Displays a scrollable list of user's casefiles
- Allows quick navigation between cases without returning to dashboard
- Shows current active casefile
- Provides search/filter functionality
Interplay:
- Used by CasefileDetail component for file explorer-style navigation
- Fetches user's casefiles from Firebase
*/
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { FaSearch, FaFolder, FaFolderOpen, FaPlus } from 'react-icons/fa';
import './CasefileNavigationSidebar.css';

const CasefileNavigationSidebar = ({ user, isOpen, onToggle }) => {
  const navigate = useNavigate();
  const { casefileId } = useParams();
  const [casefiles, setCasefiles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Fetch user's casefiles
  useEffect(() => {
    if (!user?.uid) {
      setCasefiles([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const q = query(
      collection(db, 'casefiles'),
      where('createdBy', '==', user.uid),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const files = [];
      querySnapshot.forEach((doc) => {
        files.push({ id: doc.id, ...doc.data() });
      });
      setCasefiles(files);
      setIsLoading(false);
    }, (error) => {
      console.error('Error fetching casefiles:', error);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [user?.uid]);

  // Filter casefiles based on search term
  const filteredCasefiles = useMemo(() => {
    if (!searchTerm.trim()) return casefiles;
    
    const term = searchTerm.toLowerCase();
    return casefiles.filter(casefile => 
      casefile.title?.toLowerCase().includes(term) ||
      casefile.description?.toLowerCase().includes(term) ||
      casefile.categories?.some(cat => cat.toLowerCase().includes(term))
    );
  }, [casefiles, searchTerm]);

  const handleCasefileSelect = (selectedCasefileId) => {
    if (selectedCasefileId !== casefileId) {
      navigate(`/casefile/${selectedCasefileId}`);
    }
    // Close sidebar on mobile after selection
    if (window.innerWidth <= 768) {
      onToggle(false);
    }
  };

  const handleCreateNew = () => {
    navigate('/dashboard');
    // Close sidebar on mobile
    if (window.innerWidth <= 768) {
      onToggle(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
      });
    } catch (error) {
      return '';
    }
  };

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div 
          className="casefile-nav-overlay" 
          onClick={() => onToggle(false)}
        />
      )}
      
      <div className={`casefile-navigation-sidebar ${isOpen ? 'open' : ''}`}>
        {/* Header */}
        <div className="nav-sidebar-header">
          <h3>Your Cases</h3>
          <button
            className="create-new-btn"
            onClick={handleCreateNew}
            title="Create New Casefile"
          >
            <FaPlus />
          </button>
        </div>

        {/* Search */}
        <div className="nav-sidebar-search">
          <div className="search-input-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search cases..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        {/* Casefile List */}
        <div className="nav-sidebar-content">
          {isLoading ? (
            <div className="nav-sidebar-loading">
              <p>Loading cases...</p>
            </div>
          ) : filteredCasefiles.length === 0 ? (
            <div className="nav-sidebar-empty">
              {searchTerm ? (
                <p>No cases match your search.</p>
              ) : (
                <div>
                  <p>No cases yet.</p>
                  <button 
                    className="create-first-btn"
                    onClick={handleCreateNew}
                  >
                    Create your first case
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="casefile-list">
              {filteredCasefiles.map((casefile) => {
                const isActive = casefile.id === casefileId;
                const IconComponent = isActive ? FaFolderOpen : FaFolder;
                
                return (
                  <div
                    key={casefile.id}
                    className={`casefile-item ${isActive ? 'active' : ''}`}
                    onClick={() => handleCasefileSelect(casefile.id)}
                  >
                    <div className="casefile-item-icon">
                      <IconComponent />
                    </div>
                    <div className="casefile-item-content">
                      <div className="casefile-item-title">
                        {casefile.title || 'Untitled Case'}
                      </div>
                      <div className="casefile-item-meta">
                        <span className="casefile-date">
                          {formatDate(casefile.createdAt)}
                        </span>
                        {casefile.categories && casefile.categories.length > 0 && (
                          <span className="casefile-category">
                            {casefile.categories[0]}
                          </span>
                        )}
                        {casefile.isPublic && (
                          <span className="casefile-public">Public</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="nav-sidebar-footer">
          <div className="casefile-count">
            {filteredCasefiles.length} {filteredCasefiles.length === 1 ? 'case' : 'cases'}
            {searchTerm && ` (filtered)`}
          </div>
        </div>
      </div>
    </>
  );
};

export default CasefileNavigationSidebar;
